import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { Account } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';

interface AccountsState {
  accounts: Account[];
  isLoading: boolean;
  error: string | null;
  syncStatus: Record<string, 'syncing' | 'success' | 'error'>;
}

const initialState: AccountsState = {
  accounts: [],
  isLoading: false,
  error: null,
  syncStatus: {},
};

// Async thunks
export const fetchAccounts = createAsyncThunk(
  'accounts/fetchAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.accounts.list);
      // [T27P1] Backend returns { success, data: [...], count }. Must unwrap .data
      // to get the accounts array. Previously returned the full wrapper object,
      // breaking array operations (map, filter, length) on accounts state.
      return (response.data.data || response.data) as Account[];
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch accounts'));
    }
  }
);

export const createAccount = createAsyncThunk(
  'accounts/createAccount',
  async (accountData: Partial<Account>, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.accounts.create, accountData);
      return (response.data.data || response.data) as Account;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to create account'));
    }
  }
);

export const updateAccount = createAsyncThunk(
  'accounts/updateAccount',
  async ({ id, data }: { id: string; data: Partial<Account> }, { rejectWithValue }) => {
    try {
      const response = await api.put(endpoints.accounts.update(id), data);
      return (response.data.data || response.data) as Account;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update account'));
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'accounts/deleteAccount',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.delete(endpoints.accounts.delete(id));
      return id;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete account'));
    }
  }
);

export const syncAccount = createAsyncThunk(
  'accounts/syncAccount',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.accounts.sync(id));
      return (response.data.data || response.data) as Account;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to sync account'));
    }
  }
);

// Slice
const accountsSlice = createSlice({
  name: 'accounts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    updateAccountBalance: (state, action: PayloadAction<{ id: string; balance: number }>) => {
      const account = state.accounts.find(acc => acc.id === action.payload.id);
      if (account) {
        account.balance = action.payload.balance;
        account.updatedAt = new Date().toISOString();
      }
    },
    // [T68P1-c] Optimistic update actions for offline sync
    addOptimisticAccount: (state, action: PayloadAction<Account>) => {
      state.accounts.unshift(action.payload);
    },
    replaceOptimisticAccount: (state, action: PayloadAction<{ optimisticId: string; realItem: Account }>) => {
      const idx = state.accounts.findIndex(a => a.id === action.payload.optimisticId);
      if (idx !== -1) state.accounts[idx] = action.payload.realItem;
    },
    removeOptimisticAccount: (state, action: PayloadAction<string>) => {
      state.accounts = state.accounts.filter(a => a.id !== action.payload);
    },
  },
  extraReducers: (builder) => {
    // Fetch accounts
    builder
      .addCase(fetchAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accounts = action.payload;
      })
      .addCase(fetchAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create account
    builder
      .addCase(createAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.accounts.push(action.payload);
      })
      .addCase(createAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update account
    builder
      .addCase(updateAccount.fulfilled, (state, action) => {
        const index = state.accounts.findIndex(acc => acc.id === action.payload.id);
        if (index !== -1) {
          state.accounts[index] = action.payload;
        }
      })
      .addCase(updateAccount.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Delete account
    builder
      .addCase(deleteAccount.fulfilled, (state, action) => {
        state.accounts = state.accounts.filter(acc => acc.id !== action.payload);
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.error = action.payload as string;
      });

    // Sync account
    builder
      .addCase(syncAccount.pending, (state, action) => {
        state.syncStatus[action.meta.arg] = 'syncing';
      })
      .addCase(syncAccount.fulfilled, (state, action) => {
        state.syncStatus[action.payload.id] = 'success';
        const account = state.accounts.find(acc => acc.id === action.payload.id);
        if (account) {
          account.lastSyncedAt = action.payload.lastSyncedAt;
        }
      })
      .addCase(syncAccount.rejected, (state, action) => {
        state.syncStatus[action.meta.arg] = 'error';
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError, updateAccountBalance,
  addOptimisticAccount, replaceOptimisticAccount, removeOptimisticAccount,
} = accountsSlice.actions;
export default accountsSlice.reducer;
